import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import type { EReservationStatuses } from '@timeedit/types/lib/enums';
import api from '../services/api.service';
import { message } from '../utils/messageUtils';
import {
  type TTEPreferencesViewLink,
  VIEW_LINK_ENDPOINT,
} from './appsPrefReviewSettings';

interface AutomaticAllocationFrequency {
  daily?: boolean;
  hourly?: boolean;
  everyMinute?: boolean;
}

interface AutomaticAllocation {
  frequency?: AutomaticAllocationFrequency;
  deAllocationEnabled?: boolean;
  allocationEnabled?: boolean;
  fromWaitlistEnabled?: boolean;
}

export interface AllocateSettings {
  automaticAllocation?: AutomaticAllocation;
  reservationStatuses?: EReservationStatuses[];
  blockStudentConflict?: boolean;
  enableWaitlist?: boolean;
}

type AllocateSettingsState = {
  loading: boolean;
  hasErrors: boolean;
  settings: AllocateSettings;
};

export const initialState: AllocateSettingsState = {
  loading: false,
  hasErrors: false,
  settings: {},
};

// Slice prefReviewSettings
const slice = createSlice({
  name: 'appsAllocateSettings',
  initialState,
  reducers: {
    fetchData: (state) => {
      isLoadingRequest(state);
    },
    fetchDataSuccess: (state, { payload }: PayloadAction<AllocateSettings>) => {
      state.settings = payload;
      finishedLoadingSuccess(state);
    },
    fetchDataFailure: (state) => {
      finishedLoadingFailure(state);
    },
  },
});

export default slice.reducer;

// Selectors
export const AllocateSettingsLoading = (state: TRootState) =>
  state.appsAllocateSettings.loading;
export const AllocateSettingsSelector = (state: TRootState) =>
  state.appsAllocateSettings.settings;

// Actions
export const { fetchData, fetchDataSuccess, fetchDataFailure } = slice.actions;

const ENDPOINT = '/apps/TE_ALLOCATE/allocate-settings';

export const getAllocateSettings = createAppAsyncThunk(
  'appsAllocateSettings/getAllocateSettings',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchData());
      const data = await api.get({
        endpoint: ENDPOINT,
      });
      dispatch(fetchDataSuccess(data));
    } catch (e) {
      dispatch(fetchDataFailure());
      console.error(e);
    }
  }
);

export const updateAllocateSettings = createAppAsyncThunk<
  void,
  AllocateSettings
>(
  'appsAllocateSettings/updateAllocateSettings',
  async (dataBody, { dispatch }) => {
    try {
      await api.patch({
        endpoint: ENDPOINT,
        data: dataBody,
        successMessage: 'Settings saved',
      });
      dispatch(getAllocateSettings());
    } catch (e) {
      dispatch(fetchDataFailure());
      console.error(e);
    }
  }
);

export const updateReservationFilterSettings = createAppAsyncThunk<
  void,
  { allocateSettings: AllocateSettings; viewLink: TTEPreferencesViewLink }
>(
  'appsAllocateSettings/updateReservationFilterSettings',
  async (dataBody, { dispatch }) => {
    try {
      await Promise.all([
        api.patch({
          endpoint: ENDPOINT,
          data: dataBody.allocateSettings,
          successMessage: '',
        }),
        api.patch({
          endpoint: VIEW_LINK_ENDPOINT,
          data: dataBody.viewLink,
          successMessage: '',
        }),
      ]);

      message.success({
        content: 'Settings saved',
      });
      dispatch(getAllocateSettings());
    } catch (e) {
      dispatch(fetchDataFailure());
      console.error(e);
    }
  }
);
